import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth.store';
import { toast } from '../../stores/toast.store';
import { Icon } from '../../components/ui/Icon';
import { Breadcrumb } from '../../components/ui/Breadcrumb';
import { Chip } from '../../components/ui/Chip';
import { Avatar } from '../../components/ui/Avatar';
import { ActionsMenu, type ActionItem } from '../../components/ui/ActionsMenu';
import { DetailSkeleton } from '../../components/ui/Skeleton';
import { ModuleKey, PermissionLevel, hasAtLeast } from '@masamune/shared';
import { fmtDate } from '../../lib/utils';
import {
  taskDetailQuery, assignableUsersQuery, clientsLiteQuery,
  RECURRENCES, RECURRENCE_LABEL, PRIORITY_LABEL,
  dueInfo, toDateInput, toDateTimeInput, fromDateTimeInput, fmtDateTime, fullName, isDone, isHigh,
  googleCalendarUrl, downloadTaskIcs,
  type TaskRecurrenceKey,
} from './tasksApi';
import { useTaskActions } from './useTaskActions';
import './tasks.css';

/**
 * Dettaglio task — pagina compatta a una colonna, nello stile dei dettagli
 * della Contabilità (header editoriale + righe chiave-valore nude).
 * Solo l'essenziale: titolo, assegnato a, scadenza, promemoria, ricorrenza,
 * priorità, descrizione, cliente. Ogni campo salva al cambio (PATCH parziale).
 * Azioni: Completa/Riapri in evidenza; Google Calendar, .ics, Elimina nel menu.
 */
export function TaskDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const { complete, reopen, update, remove } = useTaskActions();

  const hasWrite = useMemo(() => {
    if (!user) return false;
    const lvl = user.permissions.find((p) => p.module === ModuleKey.TASKS)?.level ?? PermissionLevel.NONE;
    return hasAtLeast(lvl, PermissionLevel.WRITE);
  }, [user]);

  const { data: task, isLoading, isError } = useQuery({ ...taskDetailQuery(id ?? ''), enabled: !!id, retry: false });
  // Per riga decide l'API (creatore, assegnatario o admin TASKS): canManage false = sola lettura
  const canWrite = hasWrite && (task?.canManage ?? true);
  const { data: users } = useQuery(assignableUsersQuery);
  const { data: clientsData } = useQuery(clientsLiteQuery);

  if (!id) return <Navigate to="/tasks" replace />;
  if (isError) return (
    <div className="page">
      <Breadcrumb items={[{ label: 'Task', to: '/tasks' }, { label: 'Dettaglio' }]} />
      <div className="max-w-[480px] mx-auto py-20 text-center">
        <h1 className="t-h1">Task non trovato</h1>
        <div className="mute mt-2">Forse è stato eliminato, o il link è sbagliato.</div>
      </div>
    </div>
  );
  if (isLoading || !task) return <DetailSkeleton header="chips" kpis={0} cards={1} />;

  const done = isDone(task.status);
  const due = dueInfo(task.dueDate, done);
  const gcal = googleCalendarUrl(task);
  const patch = (data: Record<string, unknown>) => update.mutate({ id: task.id, data });

  const onDelete = async () => {
    const r = await remove.mutateAsync({ id: task.id, title: task.title });
    if (r) navigate('/tasks');
  };
  const onIcs = () => {
    downloadTaskIcs(task.id, task.title).catch((e: any) => toast.alert('File non scaricato', e?.message ?? 'Riprova'));
  };

  const actionItems: ActionItem[] = [
    { key: 'gcal', label: 'Aggiungi a Google Calendar', icon: 'calendarPlus', hidden: !gcal, onClick: () => window.open(gcal!, '_blank', 'noopener') },
    { key: 'ics', label: 'Scarica .ics', icon: 'export', hidden: !task.dueDate && !task.remindAt, onClick: onIcs },
    { key: 'delete', label: 'Elimina', icon: 'trash', danger: true, hidden: !canWrite, onClick: onDelete },
  ];

  return (
    // m-detail: sotto i 768px la scheda diventa una colonna sola e le coppie
    // etichetta/valore mettono l'etichetta sopra il valore. Sul computer la
    // classe non dichiara niente (os-planning/mobile.md §7).
    <div className="page m-detail">
      <Breadcrumb items={[{ label: 'Task', to: '/tasks' }, { label: task.title }]} />

      <div className="page-ed-hd flex items-end justify-between gap-4">
        <div className="min-w-0 max-w-[760px] flex-1">
          <TitleEditor title={task.title} canWrite={canWrite} done={done} onSave={(title) => patch({ title })} />
          <div className="detail-meta mt-2 truncate">
            {task.createdBy && <span><span className="mute-2 mr-1.5">Creato da</span><span className="v">{fullName(task.createdBy)}</span></span>}
            {task.createdAt && <><span className="mute-2">·</span><span className="mono">{fmtDate(task.createdAt)}</span></>}
            {done && task.completedAt && <><span className="mute-2">·</span><span><span className="mute-2 mr-1.5">Completato</span><span className="v mono">{fmtDateTime(task.completedAt)}</span></span></>}
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2.5 items-center">
            <Chip tone={done ? 'ok' : 'info'} dot>{done ? 'Completato' : 'Da fare'}</Chip>
            {task.dueDate && !done && (
              <Chip tone={due.tone === 'alert' ? 'alert' : due.tone === 'warn' ? 'warn' : 'ghost'}>
                <Icon name="calendar" size={10} /> {due.tone === 'mute' ? fmtDate(task.dueDate) : due.label}
              </Chip>
            )}
            {isHigh(task.priority) && <Chip tone="alert">Priorità alta</Chip>}
            {task.recurrence && task.recurrence !== 'NONE' && <Chip tone="ghost"><Icon name="repeat" size={10} /> {RECURRENCE_LABEL[task.recurrence]}</Chip>}
          </div>
        </div>

        {/* d-only sul bottone principale: sul telefono Completa/Riapri sta
            nella barra in fondo, dove arriva il pollice. Il menu resta qui
            perché è roba secondaria, e perché una tendina attaccata al bordo
            di sotto si aprirebbe fuori schermo (os-planning/mobile.md §4). */}
        <div className="flex gap-2 pb-1 shrink-0">
          {canWrite && (done ? (
            <button className="btn d-only" disabled={reopen.isPending} onClick={() => reopen.mutate({ id: task.id, title: task.title })}>
              <Icon name="undo" size={12} /> Riapri
            </button>
          ) : (
            <button className="btn btn-primary d-only" disabled={complete.isPending} onClick={() => complete.mutate({ id: task.id, title: task.title, prevStatus: task.status, dueDate: task.dueDate, recurrence: task.recurrence })}>
              <Icon name="check" size={12} /> Completa
            </button>
          ))}
          <ActionsMenu variant="ghost" label="Azioni" items={actionItems} disabled={remove.isPending} />
        </div>
      </div>

      {/* Una colonna compatta: righe chiave-valore nude, salvataggio al cambio */}
      <div className="tk-detail">
        <div className="data-hd">
          <span className="t-micro">Dettagli</span>
          {update.isPending && <span className="mute text-[12px]">Salvo…</span>}
        </div>
        <div className="ed-kv">
          <KvRow label="Assegnato a">
            {canWrite ? (
              <select className="inp tk-inline" value={task.assignee?.id ?? ''} onChange={(e) => patch({ assigneeId: e.target.value || null })}>
                <option value="">Nessuno</option>
                {(users ?? []).map((u) => <option key={u.id} value={u.id}>{fullName(u)}{u.id === user?.id ? ' (io)' : ''}</option>)}
              </select>
            ) : task.assignee ? <span className="row" style={{ gap: 6 }}><Avatar initials={task.assignee.initials} size={20} /> {fullName(task.assignee)}</span> : '—'}
          </KvRow>
          <KvRow label="Scadenza">
            {canWrite
              ? <input className="inp tk-inline mono" type="date" value={toDateInput(task.dueDate)} onChange={(e) => patch({ dueDate: e.target.value || null })} />
              : <span className={`mono ${due.tone === 'alert' ? 'text-alert' : ''}`}>{fmtDate(task.dueDate)}</span>}
          </KvRow>
          <KvRow label="Promemoria" hint="facoltativo, con l'ora">
            {canWrite
              ? <input className="inp tk-inline mono" type="datetime-local" value={toDateTimeInput(task.remindAt)} onChange={(e) => patch({ remindAt: fromDateTimeInput(e.target.value) })} />
              : <span className="mono">{fmtDateTime(task.remindAt)}</span>}
          </KvRow>
          <KvRow label="Ricorrenza" hint={task.recurrence && task.recurrence !== 'NONE' ? 'al Completa si crea il prossimo' : undefined}>
            {canWrite ? (
              <select className="inp tk-inline" value={task.recurrence ?? 'NONE'} onChange={(e) => patch({ recurrence: e.target.value as TaskRecurrenceKey })}>
                {RECURRENCES.map((r) => <option key={r} value={r}>{RECURRENCE_LABEL[r]}</option>)}
              </select>
            ) : RECURRENCE_LABEL[task.recurrence ?? 'NONE']}
          </KvRow>
          <KvRow label="Priorità">
            {canWrite ? (
              <div className="seg-control tk-seg-sm">
                <button aria-pressed={!isHigh(task.priority)} onClick={() => { if (isHigh(task.priority)) patch({ priority: 'MEDIA' }); }}>Normale</button>
                <button aria-pressed={isHigh(task.priority)} onClick={() => { if (!isHigh(task.priority)) patch({ priority: 'ALTA' }); }}>Alta</button>
              </div>
            ) : PRIORITY_LABEL[task.priority]}
          </KvRow>
        </div>

        <div className="ed-divider" />

        <DescriptionBlock description={task.description} canWrite={canWrite} onSave={(description) => patch({ description })} />

        <div className="ed-divider" />

        <div className="ed-kv">
          <KvRow label="Cliente" hint="facoltativo">
            {canWrite ? (
              <select className="inp tk-inline" value={task.client?.id ?? ''} onChange={(e) => patch({ clientId: e.target.value || null })}>
                <option value="">Nessuno</option>
                {(clientsData?.items ?? []).map((c) => <option key={c.id} value={c.id}>{c.shortName}</option>)}
              </select>
            ) : (task.client?.shortName ?? '—')}
          </KvRow>
          {task.project && (
            <KvRow label="Progetto">
              <a className="mono hover:underline cursor-pointer" onClick={() => navigate(`/projects/${task.project!.id}`)}>{task.project.code} · {task.project.name}</a>
            </KvRow>
          )}
        </div>
      </div>

      {/* Telefono: l'azione per cui si apre un task dal telefono, appoggiata
          sopra la barra di navigazione. m-only la spegne sul computer, dove la
          stessa azione sta già in cima. */}
      {canWrite && (
        <div className="m-actionbar m-only">
          {done ? (
            <button className="btn" disabled={reopen.isPending} onClick={() => reopen.mutate({ id: task.id, title: task.title })}>
              <Icon name="undo" size={12} /> Riapri
            </button>
          ) : (
            <button className="btn btn-primary" disabled={complete.isPending} onClick={() => complete.mutate({ id: task.id, title: task.title, prevStatus: task.status, dueDate: task.dueDate, recurrence: task.recurrence })}>
              <Icon name="check" size={12} /> Completa
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Titolo modificabile: click sul titolo → input della stessa taglia
// ════════════════════════════════════════════════════════════════════
function TitleEditor({ title, canWrite, done, onSave }: { title: string; canWrite: boolean; done: boolean; onSave: (t: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(title);
  useEffect(() => { setValue(title); }, [title]);

  const commit = () => {
    setEditing(false);
    const v = value.trim();
    if (!v) { setValue(title); return; }
    if (v !== title) onSave(v);
  };

  if (editing) {
    return (
      <input
        className="ed-title tk-title-input"
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          else if (e.key === 'Escape') { setValue(title); setEditing(false); }
        }}
        aria-label="Titolo del task"
      />
    );
  }
  return (
    <h1
      className={`ed-title${canWrite ? ' tk-title-editable' : ''}${done ? ' line-through' : ''}`}
      title={canWrite ? 'Clicca per modificare' : undefined}
      onClick={() => { if (canWrite) setEditing(true); }}
    >
      {title}
    </h1>
  );
}

function KvRow({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="ed-kv-row">
      <div className="k">{label}{hint && <div className="tk-kv-hint">{hint}</div>}</div>
      <div className="v">{children}</div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Descrizione: blocco nudo con composer inline (come le note della fattura)
// ════════════════════════════════════════════════════════════════════
function DescriptionBlock({ description, canWrite, onSave }: { description: string | null; canWrite: boolean; onSave: (d: string | null) => void }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  return (
    <div>
      <div className="data-hd">
        <span className="t-micro">Descrizione <span className="mute-2 normal-case tracking-normal font-medium">· facoltativa</span></span>
        {canWrite && !open && (
          <button className="btn btn-sm btn-ghost" onClick={() => { setText(description ?? ''); setOpen(true); }}>
            <Icon name={description ? 'file' : 'plus'} size={12} /> {description ? 'Modifica' : 'Aggiungi'}
          </button>
        )}
      </div>
      {open ? (
        <>
          <textarea className="inp" rows={4} autoFocus placeholder="Di cosa si tratta, cosa serve per chiuderlo…" value={text} onChange={(e) => setText(e.target.value)} />
          <div className="flex gap-2 mt-2">
            <button className="btn btn-sm" onClick={() => { onSave(text.trim() || null); setOpen(false); }}>Salva</button>
            <button className="btn btn-sm btn-ghost" onClick={() => setOpen(false)}>Annulla</button>
          </div>
        </>
      ) : description ? (
        <div className="whitespace-pre-wrap leading-relaxed">{description}</div>
      ) : (
        <div className="mute text-[13px]">Nessuna descrizione.</div>
      )}
    </div>
  );
}
